import type { AcademyAuthority, SubscribeItem } from '@mathflat/domain/Academy'

/**
 * 구독(플랜) 기반 페이지 내 기능 플래그 + SOLO 구독 검증.
 *
 * **라우트 접근** 자체는 FeaturePermission (ROUTE_PERMISSION_MAP + restrictions) 이 담당.
 * 이 클래스는 라우트에 진입한 뒤 **페이지 내부의 섹션/기능 노출**을 결정한다.
 *
 * - PREMIUM: 모든 기능 활성화
 * - BASIC: PREMIUM 전용 기능 비활성화, 구독 검증 없음
 * - SOLO: BASIC과 동일 + 학습지/교재/강의 접근 시 구독 검증
 */
export class SubscriptionPermission {
  private readonly _academyAuthority: AcademyAuthority
  private readonly _subscribeItems: SubscribeItem[] | null

  constructor(params: {
    academyAuthority: AcademyAuthority
    subscribeItems: SubscribeItem[] | null
  }) {
    this._academyAuthority = params.academyAuthority
    this._subscribeItems = params.subscribeItems
  }

  // --- 플랜 타입 ---

  get isPremium(): boolean {
    return this._academyAuthority === 'PREMIUM'
  }

  get isBasic(): boolean {
    return this._academyAuthority === 'BASIC'
  }

  get isSolo(): boolean {
    return this._academyAuthority === 'SOLO'
  }

  // --- 구독 커리큘럼 ---

  /** SOLO 구독 커리큘럼 항목 (trieKeys 기반 검증 등에 활용) */
  get subscribedCurriculums(): SubscribeItem[] | null {
    return this._subscribeItems
  }

  // --- SOLO 구독 검증 ---

  /**
   * SOLO 구독 상태 검증 (union 파라미터).
   *
   * 우선순위:
   * 1. `trieKey` 가 있으면 trieKey 매칭 (가장 정확 — 고등 과목 단위까지 검증)
   * 2. `schoolType + grade` 폴백 (초등/중등용. 고등은 grade 무관 schoolType 만)
   * 3. `schoolType` 만 있으면 schoolType 매칭
   *
   * - PREMIUM/BASIC: 항상 true (구독 검증 불필요)
   * - SOLO + subscribeItems 없음/빈 배열: true (소수 SOLO 때문에 전체 차단 방지)
   * - SOLO + subscribeItems 있음: 위 우선순위로 매칭
   */
  isSubscribed(
    params:
      | { trieKey: string | null }
      | { schoolType: 'ELEMENTARY' | 'MIDDLE' | 'HIGH'; grade?: number | null },
  ): boolean {
    if (!this.isSolo) return true
    if (!this._subscribeItems?.length) return true

    if ('trieKey' in params) {
      if (!params.trieKey) return true
      return this._isSubscribedByTrieKey(params.trieKey)
    }
    return this._isSubscribedByGrade(params.schoolType, params.grade ?? null)
  }

  private _isSubscribedByTrieKey(trieKey: string): boolean {
    return this._subscribeItems?.some((item) => item.trieKeys?.includes(trieKey)) ?? false
  }

  private _isSubscribedByGrade(
    schoolType: 'ELEMENTARY' | 'MIDDLE' | 'HIGH',
    grade: number | null,
  ): boolean {
    return (
      this._subscribeItems?.some((item) => {
        if (item.schoolType !== schoolType) return false
        // 고등이거나, item.grade가 null이면 schoolType만으로 매칭 (과목 단위 검증은 trieKey 분기에서)
        if (schoolType === 'HIGH' || item.grade == null) return true
        return grade == null || String(item.grade) === String(grade)
      }) ?? false
    )
  }
}
